// Purchase invoice import draft API (phase 1).
//
// Exact backend contract in backend/src/routes/purchaseInvoices.js (mounted at
// /api/purchase-invoices): every endpoint requires invoice import management
// (fail-closed Emil/Tomek allowlist). Draft endpoints never approve, never
// create PZ/inventory and never settle — no approval call exists here because
// no approval route exists in this phase. Payloads forward caller input
// verbatim; this module invents no document numbers, totals or FX.
import api from '../../../services/api';
import {
  normalizePurchaseInvoiceDraft,

} from '../../../dto/workflowInvoice';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';

export type PurchaseInvoiceDraft = WorkflowInvoice;

export interface PurchaseInvoiceDraftSearch {
  status?: string;
  supplierOrderId?: string;
  supplierId?: string;
  documentNumber?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export interface ExtractionRequestResult {
  seq: number;
  canonical?: unknown;
}

export interface ApplyExtractionInput {
  seq: number;
  expectedVersion: number;
  overwriteManualEdits?: boolean;
}

export async function createPurchaseInvoiceDraft(
  input: Record<string, unknown>,
): Promise<PurchaseInvoiceDraft> {
  const response = await api.post('/purchase-invoices', input);
  return normalizePurchaseInvoiceDraft(response.data, 'purchaseInvoiceDraft.create');
}

export async function searchPurchaseInvoiceDrafts(
  query: PurchaseInvoiceDraftSearch = {},
): Promise<PurchaseInvoiceDraft[]> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get(`/purchase-invoices${params.toString() ? `?${params.toString()}` : ''}`);
  return (Array.isArray(response.data) ? response.data : []).map(item => normalizePurchaseInvoiceDraft(item, 'purchaseInvoiceDraft.search'));
}

export async function fetchPurchaseInvoiceDraft(id: string): Promise<PurchaseInvoiceDraft> {
  const response = await api.get(`/purchase-invoices/${id}`);
  return normalizePurchaseInvoiceDraft(response.data, 'purchaseInvoiceDraft.detail');
}

export async function updatePurchaseInvoiceDraft(
  id: string,
  input: Record<string, unknown>,
  expectedVersion: number,
): Promise<PurchaseInvoiceDraft> {
  const response = await api.put(`/purchase-invoices/${id}`, { ...input, expectedVersion });
  return normalizePurchaseInvoiceDraft(response.data, 'purchaseInvoiceDraft.update');
}

export async function deletePurchaseInvoiceDraft(id: string): Promise<void> {
  await api.delete(`/purchase-invoices/${id}`);
}

export async function attachPurchaseInvoiceSourceFile(
  id: string,
  file: File,
): Promise<PurchaseInvoiceDraft> {
  const payload = new FormData();
  payload.append('file', file);
  const response = await api.post(`/purchase-invoices/${id}/files`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizePurchaseInvoiceDraft(response.data, 'purchaseInvoiceDraft.attachFile');
}

export async function downloadPurchaseInvoiceSourceFile(id: string, sha256?: string): Promise<Blob> {
  const response = await api.get(`/purchase-invoices/${id}/file`, {
    params: sha256 ? { sha256 } : undefined,
    responseType: 'blob',
  });
  return response.data;
}

export async function requestPurchaseInvoiceExtraction(
  id: string,
  files: File[],
): Promise<ExtractionRequestResult> {
  const payload = new FormData();
  files.forEach((file) => payload.append('files', file));
  const response = await api.post(`/purchase-invoices/${id}/extract`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function applyPurchaseInvoiceExtraction(
  id: string,
  input: ApplyExtractionInput,
): Promise<PurchaseInvoiceDraft> {
  const response = await api.post(`/purchase-invoices/${id}/apply-extraction`, input);
  return normalizePurchaseInvoiceDraft(response.data, 'purchaseInvoiceDraft.applyExtraction');
}
